import React, { useState } from 'react';
import { TableCell } from 'components/mui';
import { TextField } from '@mui/material';
import { useTimeInputState } from 'containers/pages/comments/hooks/useTimeInputState';
import { formatDecimalTime } from 'containers/pages/comments/utils/formatDecimalTime';
import { CANtrackComment } from 'containers/pages/comments/types/comment';
import useSaveComment from 'containers/pages/comments/hooks/useSaveComment';
import { useNotificationProvider } from 'hooks';

export const TimeSpentEditableCell: React.FC<{
	comment: CANtrackComment;
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>;
	showDecimalFormat: boolean;
}> = ({ comment, setComments, showDecimalFormat }) => {
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const { timeRegular, timeOver } = comment;
	const { saveComment } = useSaveComment(setComments);
	const { notifyError } = useNotificationProvider();
	const {
		value: updatedRegularTime,
		error: updatedRegularTimeError,
		handleChange: handleChangeRegularTime,
	} = useTimeInputState(timeRegular);

	const regularTime: string = showDecimalFormat
		? `${timeRegular.toFixed(2)}`
		: formatDecimalTime(timeRegular);

	const overTime: string = showDecimalFormat
		? `${timeOver.toFixed(2)}`
		: formatDecimalTime(timeOver);

	const handleKeyPress = (e: any) => {
		if (e.key === 'Enter') {
			handleBlur();
		}
		if (e.key === 'Escape') {
			handleChangeRegularTime(timeRegular.toString());
			setIsEditing(false);
		}
	};

	const handleBlur = () => {
		if (updatedRegularTime !== comment.timeRegular) {
			if (!updatedRegularTimeError) {
				saveComment(
					{
						...comment,
						timeRegular: updatedRegularTime,
					}, // update the timeRegular property of the comment object
					() => setIsEditing(false), // callback to set isEditing to false
				);
			} else {
				setIsEditing(false);
				handleChangeRegularTime(timeRegular.toString());
				notifyError('Invalid time entry.');
			}
		} else {
			setIsEditing(false);
		}
	};

	return (
		<TableCell
			align="left"
			onClick={() => setIsEditing(true)}
			sx={{ width: '100px' }}
		>
			{isEditing ? (
				<TextField
					type="number"
					value={updatedRegularTime}
					onChange={(e) => handleChangeRegularTime(e.target.value)}
					error={!!updatedRegularTimeError}
					helperText={updatedRegularTimeError || ''}
					label="Regular Time"
					variant="outlined"
					sx={{ width: '150px' }}
					inputProps={{ step: 0.25 }} // increment by 0.25 or 15 minutes
					onBlur={handleBlur}
					onKeyUp={handleKeyPress}
					autoFocus
				/>
			) : (
				<span>
					{timeOver > 0 ? (
						<span>
							{regularTime}
							&nbsp;{'+'}
							&nbsp;
							{overTime}
							&nbsp;OT
						</span>
					) : (
						regularTime
					)}
				</span>
			)}
		</TableCell>
	);
};
